import { useNavigate } from 'react-router-dom';

import { Tier, isStandardTrack, TIER_TO_TRACK_CODE, TrackCode } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { TRACK_IMAGES } from '@backrs_ui/grow-ui/src/assets/careerTrackImages';
import { CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { getLastBlueprintFormattedDate } from '@backrs_ui/grow-ui/src/pages/Survey/survey.util';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.survey.output.blueprintSection.minimumTrack.detail;
const DIAGRAM_STRINGS = appStrings.modules.results.diagram;

type MinimumTrackDetailProps = Readonly<{
  tier: Tier;
  userResponseSet: CareerUserResponseSet;
}>;

export function MinimumTrackDetail({ tier, userResponseSet }: MinimumTrackDetailProps) {
  const navigate = useNavigate();
  const trackCode: TrackCode = TIER_TO_TRACK_CODE[tier];
  const trackImage = isStandardTrack(trackCode) ? TRACK_IMAGES[trackCode] : undefined;
  const formattedDate = getLastBlueprintFormattedDate(userResponseSet);
  const tierDiagram = DIAGRAM_STRINGS[tier];
  const description = tierDiagram?.tooltip ?? '';

  return (
    <Box sx={containerSx}>
      {/* Header row: title + date + nav CTA */}
      <Box sx={headerSx}>
        <Box sx={headerLeftSx}>
          <Typography variant="display-xs-semibold">{STRINGS.heading}</Typography>
          <Typography variant="text-sm-regular" sx={dateStampSx}>
            {STRINGS.dateStamp(formattedDate)}
          </Typography>
        </Box>
        <Button
          id="minimum-track-detail-view-btn"
          hierarchy="Secondary gray"
          size="md"
          onClick={() => navigate(APP_ROUTE.MINIMUM_TRACK)}
        >
          {STRINGS.viewCta}
        </Button>
      </Box>

      {/* Two-column content: description (left) + track SVG (right) */}
      <Box sx={contentSx}>
        <Box sx={descriptionSx}>
          {description.split('\n\n').map((paragraph, i) => (
            <Typography key={i} variant="text-md-regular" sx={paragraphSx}>
              {paragraph}
            </Typography>
          ))}
        </Box>
        {trackImage && (
          <Box sx={imageSx}>
            <Box
              component="img"
              src={trackImage}
              alt={`${tier} career track illustration`}
              sx={trackImgSx}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// --- Styles ---

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  flexWrap: 'wrap',
});

const headerLeftSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const dateStampSx: SxProps<Theme> = {
  fontStyle: 'italic',
  whiteSpace: 'nowrap',
};

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  minWidth: 0,
});

const paragraphSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const imageSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  justifyContent: 'center',
};

const trackImgSx: SxProps<Theme> = {
  width: '100%',
  height: 'auto',
};
